<template>
  <ModalPage :goBackTo="{name: 'group'}" :canGoBack="canGoBack" v-slot="{finished}">
    <Mutation
      :mutation="mutation"
      :variables="variables"
      @done="onDone"
      @error="onError"
    >

    <template v-slot="{ mutate, loading }">
      <ValidationObserver
        ref="observer"
        v-slot="{ valid, invalid }"
        tag="form"
        @submit.prevent="submit(mutate)"
      >
        <div class="flex-shrink bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div class="mb-3 text-center">
            <h3 class="text-lg leading-6 font-medium text-gray-900" id="modal-headline">
              Updating <span class="underline">{{group.name}}</span>
            </h3>
          </div>

          <div class="sm:flex sm:items-start">
            <div class="sm:mt-0 sm:text-left w-full">
              <TextInput v-model="proposedName" name="Group name" label="Group name" rules="required"/>
            </div>
          </div>

          <div class="mt-6">
            <div class="mt-2 flex items-start">
              <svg
                v-if="proposedIcon || proposedColor"
                class="w-12 h-12 p-2 flex items-center justify-center text-lg rounded shadow mr-3 flex-shrink-0 mt-1"
                :class="[proposedColor ? `bg-${proposedColor.name} text-white` : 'border border-cool-gray-200 text-black']" fill="currentColor" viewBox="0 0 20 20" @click.prevent>
                  <font-awesome-icon :icon="['far', proposedIcon.name]"></font-awesome-icon>
              </svg>
              <div v-else class="w-12 h-12 border-dashed flex-shrink-0 mt-1 rounded border-4 border-cool-gray-300 mr-3">
                &nbsp;
              </div>
              <div class="ml-5 w-full">
                <div class="rounded-md shadow-sm w-full">
                  <label for="proposedIcon" class="block text-sm font-medium leading-5 text-gray-700">
                    Icon
                  </label>
                  <select id="proposedIcon" class="mt-1 block form-select w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5" v-model="proposedIcon">
                    <option :value="null">None selected</option>
                    <option v-for="icon in icons" :value="icon" :key="icon.id">
                      {{icon.displayName}}
                    </option>
                  </select>
                </div>

                <div class="rounded-md shadow-sm w-full mt-4">
                    <label for="proposedColor" class="block text-sm font-medium leading-5 text-gray-700">
                      Color
                    </label>
                    <select id="proposedColor" class="mt-1 block form-select w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5" v-model="proposedColor">
                      <option :value="null">None selected</option>
                      <option v-for="color in colors" :value="color" :key="color.id">
                        {{color.displayName}}
                      </option>
                    </select>
                </div>
              </div>
            </div>
          </div>
          <StudentSearchInput v-model="proposedStudents" :teamId="teamId" class="mt-4"/>
        </div>

      <div class="bg-gray-50 px-4 py-3 sm:px-6 flex-shrink-0 sm:flex sm:flex-row-reverse sticky bottom-0 shadow-bottom-sticky">
        <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
          <button type="submit" class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-indigo-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-indigo-500 focus:outline-none focus:border-indigo-700 focus:ring-indigo transition ease-in-out duration-150 sm:text-sm sm:leading-5">
            Save
          </button>
        </span>
        <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
          <button type="button" class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:ring-blue transition ease-in-out duration-150 sm:text-sm sm:leading-5" @click="finished">
            Cancel
          </button>
        </span>
      </div>

        </ValidationObserver>

      </template>

    </Mutation>
  </ModalPage>
</template>

<script>
import _get                   from 'lodash-es/get'
import _indexOf               from 'lodash-es/indexOf'
import _map                   from 'lodash-es/map'
import _find                  from 'lodash-es/find'
import _nth                   from 'lodash-es/nth'
import { ValidationObserver } from "vee-validate"

import Mutation               from '@/components/Mutation.vue'
import ModalPage              from '@/components/ModalPage.vue'
import TextInput              from '@/components/TextInput.vue'
import StudentSearchInput     from '@/components/StudentSearchInput.vue'

import GroupEditQuery         from '@/queries/GroupEdit.gql'
import GroupEditMutation      from '@/queries/GroupEditMutation.gql'
import ColorsQuery            from '@/queries/Colors.gql'
import IconsQuery             from '@/queries/Icons.gql'

import { preloadMany }        from '@/preloader'
import { tz }                 from '@/tz'

const preloadQueries = (to) => {
  return [
    {
      query: IconsQuery,
      queryName: 'icons',
      fetchPolicy: 'cache-first'
    },
    {
      query: ColorsQuery,
      queryName: 'colors',
      fetchPolicy: 'cache-first'
    },
    {
      query: GroupEditQuery,
      queryName: 'groupEdit',
      variables: {
        id: to.params.groupId,
        tz: tz()
      },
      fetchPolicy: 'network-only'
    }
  ]
}

export default {
  components: {
    ModalPage,
    Mutation,
    StudentSearchInput,
    TextInput,
    ValidationObserver,
  },

  props: {
    groupId: {
      required: true
    },
    teamId: {
      required: true
    },
    canGoBack: {
      type: Boolean,
      default: false
    }
  },

  beforeRouteEnter(to, from, next) {
    preloadMany({
      queries: preloadQueries(to),
      callback: next
    })
  },

  beforeRouteUpdate(to, from, next) {
    preloadMany({
      queries: preloadQueries(to),
      callback: next,
      mountedVm: this
    })
  },

  data: function () {
    return {
      group: {},
      mutation: GroupEditMutation,
      proposedName: '',
      proposedColor: null,
      proposedIcon: null,
      proposedStudents: [],
    }
  },

  computed: {

    proposedStudentIds () {
      return _map(this.proposedStudents, 'id')
    },

    variables() {
      return {
        id: this.groupId,
        name: this.proposedName,
        iconId: _get(this, 'proposedIcon.id', null),
        colorId: _get(this, 'proposedColor.id', null),
        studentIds: this.proposedStudentIds
      }
    }
  },

  methods: {

    onDone(result) {
      Bus.$emit('flash-message', {text: 'Group updated successfully', type: 'success'})
      this.$router.push({name: 'group', params: {groupId: this.groupId}})
    },

    onError(e) {
      Bus.$emit('flash-message', {text: e, type: 'error'})
    },

    async submit(mutate) {
      const isValid = await this.$refs.observer.validate()
      if (isValid) { mutate() }
    }
  },

  watch: {
    group() {
      this.proposedName = this.group.name
      this.proposedStudents = this.group.students || []
      this.proposedIcon = this.group.icon
      this.proposedColor = this.group.color
    }
  },

  apollo: {
    icons: {
      query: IconsQuery,
      fetchPolicy: 'cache-first'
    },

    colors: {
      query: ColorsQuery,
      fetchPolicy: 'cache-first'
    },

    group: {
      query: GroupEditQuery,
      variables() {
        return {
          id: this.groupId
        }
      },
      fetchPolicy: 'cache-first'
    }
  }
}
</script>
